import { useRef, useState } from "react";
import { ImagePlus, Trash2, RefreshCw, Loader, X, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { adminAPI } from "../services/admin.api";
import ImageCropModal from "../../../shared/components/ImageCropModal";
import { useImageCrop } from "../../../shared/hooks/useImageCrop";
import { useLanguage } from "../../../context/LanguageContext";
import { resolveAssetUrl } from "../../../constants/env";

const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_MB = 5;

export interface GalleryItem { id: number; url: string; }

const toUrl = resolveAssetUrl;

function validate(file: File, t: (key: string, params?: Record<string, any>) => string): string | null {
  if (!ALLOWED.includes(file.type)) return t("toast.gallery.unsupportedFormat", { name: file.name });
  if (file.size > MAX_MB * 1024 * 1024) return t("toast.gallery.exceedsSize", { name: file.name, maxMb: MAX_MB });
  return null;
}

interface Props {
  userId: string;
  initialMedia: GalleryItem[];
}

export default function AdminGallery({ userId, initialMedia }: Props) {
  const { t } = useLanguage();
  const [media, setMedia] = useState<GalleryItem[]>(initialMedia);
  const [pendingFiles, setPendingFiles] = useState<{ file: File; previewUrl: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [replacingId, setReplacingId] = useState<number | null>(null);
  const addRef = useRef<HTMLInputElement>(null);
  const replaceRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const { session: cropSession, openCrop, handleCropDone, handleCropCancel } = useImageCrop();
  const replaceCrop = useImageCrop();
  const replaceTargetRef = useRef<GalleryItem | null>(null);

  const addPending = (files: File[]) => {
    const errors: string[] = [];
    const valid = files.filter((f) => {
      const err = validate(f, t);
      if (err) { errors.push(err); return false; }
      return true;
    });
    if (errors.length) toast.error(errors[0]);
    if (!valid.length) return;
    openCrop(valid, (croppedFiles) => {
      setPendingFiles((prev) => [...prev, ...croppedFiles.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }))]);
    });
  };

  const removePending = (i: number) => {
    URL.revokeObjectURL(pendingFiles[i].previewUrl);
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleUpload = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadGalleryImages(userId, pendingFiles.map((p) => p.file));
      const newItems: GalleryItem[] = (res?.media ?? res?.data ?? []).map((m: any) => ({
        id: m.id,
        url: toUrl(m.image_url ?? m.file_path ?? m.url ?? ""),
      }));
      pendingFiles.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      setPendingFiles([]);
      setMedia((prev) => [...prev, ...newItems]);
      toast.success(t("toast.gallery.imagesUploaded", { count: newItems.length || pendingFiles.length }));
    } catch {
      toast.error(t("toast.gallery.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item: GalleryItem) => {
    if (!confirm(t("admin.gallery.confirmDelete"))) return;
    setDeletingId(item.id);
    try {
      await adminAPI.deleteGalleryImage(item.id);
      setMedia((prev) => prev.filter((m) => m.id !== item.id));
      toast.success(t("toast.gallery.imageDeleted"));
    } catch {
      toast.error(t("toast.gallery.deleteFailed"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleReplaceSelect = (item: GalleryItem, file: File) => {
    const err = validate(file, t);
    if (err) { toast.error(err); return; }
    replaceTargetRef.current = item;
    replaceCrop.openCrop([file], async ([croppedFile]) => {
      await handleReplace(item, croppedFile);
    });
  };

  const handleReplace = async (item: GalleryItem, file: File) => {
    setReplacingId(item.id);
    try {
      const { newItem } = await adminAPI.replaceGalleryImage(item.id, userId, file);
      const newUrl = newItem?.image_url ? toUrl(newItem.image_url) : URL.createObjectURL(file);
      const newId = newItem?.id ?? item.id;
      setMedia((prev) => prev.map((m) => m.id === item.id ? { id: newId, url: newUrl } : m));
      toast.success(t("toast.gallery.imageReplaced"));
    } catch {
      toast.error(t("toast.gallery.replaceFailed"));
    } finally {
      setReplacingId(null);
    }
  };

  return (
    <>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-semibold text-gray-700">{t("admin.gallery.title")}</h3>
        <button type="button" onClick={() => addRef.current?.click()}
          className="flex items-center gap-1.5 text-sm font-medium text-[#b2d12e] hover:text-[#9ab825] transition-colors">
          <ImagePlus size={16} /> {t("admin.gallery.addPhotos")}
        </button>
        <input ref={addRef} type="file" accept={ALLOWED.join(",")} multiple className="hidden"
          onChange={(e) => { addPending(Array.from(e.target.files || [])); e.target.value = ""; }} />
      </div>

      {/* Pending uploads */}
      {pendingFiles.length > 0 && (
        <div className="bg-[#b2d12e]/10 border border-[#b2d12e]/30 rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase">{t("admin.gallery.pendingUpload", { count: pendingFiles.length })}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {pendingFiles.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#b2d12e]/50">
                <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePending(i)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={handleUpload} disabled={uploading}
            className="flex items-center gap-2 bg-[#b2d12e] hover:bg-[#9ab825] text-gray-900 font-semibold px-4 py-2 rounded-xl text-sm transition-colors disabled:opacity-60">
            {uploading
              ? <><Loader size={14} className="animate-spin" /> {t("admin.gallery.uploading")}</>
              : <><Upload size={14} /> {t("admin.gallery.uploadPhotos", { count: pendingFiles.length })}</>}
          </button>
        </div>
      )}

      {/* Existing gallery */}
      {media.length === 0 && pendingFiles.length === 0 ? (
        <div onClick={() => addRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center cursor-pointer hover:border-[#b2d12e] hover:bg-[#b2d12e]/5 transition-colors">
          <ImagePlus size={32} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">{t("admin.gallery.noImages")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {media.map((item) => (
            <div key={item.id} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200">
              <img src={item.url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.png"; }} />
              {/* Overlay actions */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {/* Replace */}
                <button type="button"
                  onClick={() => replaceRefs.current[item.id]?.click()}
                  disabled={replacingId === item.id || deletingId === item.id}
                  className="bg-white/90 text-gray-800 rounded-full p-1.5 hover:bg-white transition disabled:opacity-50"
                  title={t("admin.gallery.replaceImage")}>
                  {replacingId === item.id ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </button>
                {/* Delete */}
                <button type="button"
                  onClick={() => handleDelete(item)}
                  disabled={deletingId === item.id || replacingId === item.id}
                  className="bg-red-500/90 text-white rounded-full p-1.5 hover:bg-red-600 transition disabled:opacity-50"
                  title={t("admin.gallery.deleteImage")}>
                  {deletingId === item.id ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
              <input
                ref={(el) => { replaceRefs.current[item.id] = el; }}
                type="file" accept={ALLOWED.join(",")} className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleReplaceSelect(item, f); e.target.value = ""; }}
              />
            </div>
          ))}
        </div>
      )}
    </div>

    {cropSession && (

      <ImageCropModal
        imageSrc={cropSession.src}
        aspect={1}
        fileName={cropSession.fileName}
        mimeType={cropSession.mimeType}
        title={cropSession.queue.length > 0
          ? t("admin.gallery.cropPhotoTitleWithMore", { count: cropSession.queue.length })
          : t("admin.gallery.cropPhotoTitle")}
        onCrop={handleCropDone}
        onClose={handleCropCancel}
      />
    )}

    {replaceCrop.session && (
      <ImageCropModal
        imageSrc={replaceCrop.session.src}
        aspect={1}
        fileName={replaceCrop.session.fileName}
        mimeType={replaceCrop.session.mimeType}
        title={t("admin.gallery.cropReplacementPhotoTitle")}
        onCrop={replaceCrop.handleCropDone}
        onClose={replaceCrop.handleCropCancel}
      />
    )}
    </>
  );
}
